import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import type { JSX, ReactNode } from 'react';

import { DUMMY_FOLDER_ID } from '@e-socna/flags';
import type { Id } from '@e-socna/data-layer';

import { useFindOneFolderQuery } from '../../pages/Folder/hooks/useFindOneFolderQuery.hook';

import { FolderContext } from '.';
import { FolderContextStatus } from './types/folder-context.type';
import type { FolderContextData } from './types/folder-context.type';

interface Props {
  readonly children?: ReactNode;
}

/**
 * Provides a FolderContext backed by the DUMMY_FOLDER.
 * Used for template pages (section & document) so that resolvers
 * have a valid business context when generating PDFs.
 * Does not interact with navigation context (no setPage / setFolderSpaceInfo).
 */
const DummyFolderContextProvider = ({
  children,
}: Readonly<Props>): JSX.Element => {
  const {
    data: folderQueryData,
    isLoading,
    isError,
  } = useFindOneFolderQuery(DUMMY_FOLDER_ID as Id);
  const folder = folderQueryData?.findOneFolder;

  const contextValue = useMemo<FolderContextData>(() => {
    if (isLoading || !folder) {
      return {
        status: FolderContextStatus.Loading,
        folderId: DUMMY_FOLDER_ID as Id,
        folder: undefined,
      };
    }

    if (isError) {
      return {
        status: FolderContextStatus.Error,
        folderId: DUMMY_FOLDER_ID as Id,
        folder: undefined,
      };
    }

    return {
      status: FolderContextStatus.Ready,
      folderId: DUMMY_FOLDER_ID as Id,
      folder,
    };
  }, [folder, isLoading, isError]);

  return (
    <FolderContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </FolderContext.Provider>
  );
};

export { DummyFolderContextProvider };
